import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { SectionCard } from "../../../../components/design-system/admin/SectionCard";
import { ConfirmActionDialog, StatusBadge, formatDate } from "../../../../components/design-system/admin";
import type { StatusTone } from "../../../../components/design-system/admin";
import { useWireAdminApi } from "../../../../common/hooks/use-wire-admin-api.hook";
import { InvoiceStatus, VendorInvoice } from "../../../../common/models/billing.model";
import { formatMoney } from "../../billing/money";
import { AddPastInvoiceDialog } from "./add-past-invoice.dialog";

const STATUS_TONE: Record<InvoiceStatus, StatusTone> = { pending: "warning", paid: "success", void: "neutral", expired: "neutral" };

interface VendorInvoicesPanelProps {
    vendorId: string;
    /** Pre-selected currency for a new past invoice (the vendor's plan currency). */
    defaultCurrency?: string;
}

/**
 * A vendor's invoices as a super admin sees them, and the place to add the ones
 * billed before plans existed (an uploaded PDF the vendor then finds in their own
 * billing history). Only uploaded past invoices can be deleted — a real billing
 * record never can.
 */
export const VendorInvoicesPanel = ({ vendorId, defaultCurrency }: VendorInvoicesPanelProps) => {
    const { t } = useTranslation();
    const wireAdminApi = useWireAdminApi();
    const [invoices, setInvoices] = useState<VendorInvoice[] | null>(null);
    const [adding, setAdding] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<VendorInvoice | null>(null);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(() => {
        wireAdminApi.listVendorInvoices(vendorId)
            .then((rows) => setInvoices(rows || []))
            .catch(() => { setInvoices([]); toast.error(t("ADMIN_VENDOREDIT.INVOICES.LOAD_FAILED")); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    useEffect(() => { load(); }, [load]);

    const download = (inv: VendorInvoice) =>
        wireAdminApi.downloadInvoicePdf(inv._id, inv.invoiceNumber)
            .catch(() => toast.error(t("ADMIN_VENDOREDIT.INVOICES.DOWNLOAD_FAILED")));

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await wireAdminApi.deleteHistoricalInvoice(deleteTarget._id);
            toast.success(t("ADMIN_VENDOREDIT.INVOICES.DELETED"));
            setDeleteTarget(null);
            load();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || t("ADMIN_VENDOREDIT.INVOICES.DELETE_FAILED"));
        } finally {
            setDeleting(false);
        }
    };

    const rows = [...(invoices || [])].sort((a, b) => new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime());

    return (
        <>
            <SectionCard
                title={t("ADMIN_VENDOREDIT.INVOICES.TITLE")}
                description={t("ADMIN_VENDOREDIT.INVOICES.DESCRIPTION")}
                testId="vendor-invoices-card"
                action={
                    <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                        <Plus className="size-4" />
                        {t("ADMIN_VENDOREDIT.INVOICES.ADD")}
                    </Button>
                }
            >
                {invoices === null ? (
                    <p className="text-sm text-muted-foreground">{t("ADMIN_VENDOREDIT.INVOICES.LOADING")}</p>
                ) : rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="vendor-invoices-empty">{t("ADMIN_VENDOREDIT.INVOICES.EMPTY")}</p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("ADMIN_VENDOREDIT.INVOICES.COL_NUMBER")}</TableHead>
                                <TableHead>{t("ADMIN_VENDOREDIT.INVOICES.COL_ISSUED")}</TableHead>
                                <TableHead>{t("ADMIN_VENDOREDIT.INVOICES.COL_PERIOD")}</TableHead>
                                <TableHead>{t("ADMIN_VENDOREDIT.INVOICES.COL_AMOUNT")}</TableHead>
                                <TableHead>{t("ADMIN_VENDOREDIT.INVOICES.COL_STATUS")}</TableHead>
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((inv) => (
                                <TableRow key={inv._id} data-testid={`vendor-invoice-${inv.invoiceNumber}`}>
                                    <TableCell className="font-medium">
                                        {inv.invoiceNumber}
                                        {inv.type === "historical" && (
                                            <div className="text-xs font-normal text-muted-foreground">{t("ADMIN_VENDOREDIT.INVOICES.PAST")}</div>
                                        )}
                                    </TableCell>
                                    <TableCell>{formatDate(inv.issuedAt)}</TableCell>
                                    <TableCell>{formatDate(inv.periodStart)} → {formatDate(inv.periodEnd)}</TableCell>
                                    <TableCell>{formatMoney(inv.amount, inv.currency)}</TableCell>
                                    <TableCell>
                                        <StatusBadge tone={STATUS_TONE[inv.status] || "neutral"}>
                                            {t(`ADMIN_VENDOREDIT.INVOICES.STATUS_${String(inv.status).toUpperCase()}`)}
                                        </StatusBadge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center justify-end gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                                aria-label={t("ADMIN_VENDOREDIT.INVOICES.DOWNLOAD", { number: inv.invoiceNumber })}
                                                onClick={() => download(inv)}
                                            >
                                                <Download className="size-4" />
                                            </Button>
                                            {inv.type === "historical" && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    aria-label={t("ADMIN_VENDOREDIT.INVOICES.DELETE", { number: inv.invoiceNumber })}
                                                    onClick={() => setDeleteTarget(inv)}
                                                >
                                                    <Trash2 className="size-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </SectionCard>

            <AddPastInvoiceDialog
                open={adding}
                onOpenChange={setAdding}
                vendorId={vendorId}
                defaultCurrency={defaultCurrency}
                onAdded={load}
            />

            <ConfirmActionDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                title={t("ADMIN_VENDOREDIT.INVOICES.DELETE_TITLE")}
                description={deleteTarget ? t("ADMIN_VENDOREDIT.INVOICES.DELETE_BODY", { number: deleteTarget.invoiceNumber }) : undefined}
                confirmLabel={t("ADMIN_VENDOREDIT.INVOICES.DELETE_CONFIRM")}
                destructive
                busy={deleting}
                onConfirm={confirmDelete}
            />
        </>
    );
};
